import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Spin, Descriptions, Tag, message, Empty } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { Button, Table } from '../../../design-system';
import clientAPI from '../../../service/ApiServiceClientPortal';

const BRAND_BLACK = '#1B1B1B';

const styles = {
    backRow: {
        marginBottom: 16,
    },
    title: {
        fontSize: 22,
        fontWeight: 600,
        color: BRAND_BLACK,
        margin: '0 0 4px 0',
    },
    section: {
        background: '#fff',
        borderRadius: 8,
        padding: 24,
        marginBottom: 16,
    },
    sectionTitle: {
        fontSize: 16,
        fontWeight: 600,
        color: BRAND_BLACK,
        marginBottom: 16,
    },
};

const statusColors = {
    active: 'green',
    completed: 'blue',
    on_hold: 'orange',
    cancelled: 'red',
    draft: 'default',
};

const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
        return new Date(dateStr).toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
        });
    } catch {
        return '-';
    }
};

const projectColumns = [
    {
        title: 'Project Name',
        dataIndex: 'name',
        key: 'name',
        render: (text) => <span style={{ fontWeight: 500, color: BRAND_BLACK }}>{text}</span>,
    },
    {
        title: 'Status',
        dataIndex: 'status',
        key: 'status',
        render: (status) => {
            if (!status) return '-';
            const label = status.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
            return <Tag color={statusColors[status] || 'default'}>{label}</Tag>;
        },
    },
    {
        title: 'Start Date',
        dataIndex: 'startDate',
        key: 'startDate',
        render: formatDate,
    },
    {
        title: 'End Date',
        dataIndex: 'endDate',
        key: 'endDate',
        render: formatDate,
    },
];

const ClientCompanyDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [company, setCompany] = useState(null);
    const [projects, setProjects] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchCompany = async () => {
            setLoading(true);
            try {
                const response = await clientAPI.get(`/client-portal/companies/${id}`);
                const data = response.data?.data || response.data;
                setCompany(data.company || data);
                setProjects(data.projects || data.company?.projects || []);
            } catch (err) {
                message.error('Failed to load company details');
            } finally {
                setLoading(false);
            }
        };
        fetchCompany();
    }, [id]);

    if (loading) {
        return (
            <div style={{ textAlign: 'center', padding: 80 }}>
                <Spin size="large" />
            </div>
        );
    }

    if (!company) {
        return <Empty description="Company not found" />;
    }

    const onRowClick = (record) => ({
        onClick: () => navigate(`/client-section/project/${record.id}`),
        style: { cursor: 'pointer' },
    });

    return (
        <div>
            <div style={styles.backRow}>
                <Button
                    variant="secondary"
                    icon={<ArrowLeftOutlined />}
                    onClick={() => navigate('/client-section/company')}
                >
                    Back to Companies
                </Button>
            </div>

            <div style={styles.section}>
                <h1 style={styles.title}>{company.name || 'Company'}</h1>
                <Descriptions column={{ xs: 1, sm: 2 }} size="small">
                    {company.industry && (
                        <Descriptions.Item label="Industry">{company.industry}</Descriptions.Item>
                    )}
                    {company.website && (
                        <Descriptions.Item label="Website">
                            <a
                                href={company.website}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ color: BRAND_BLACK, textDecoration: 'underline' }}
                            >
                                {company.website}
                            </a>
                        </Descriptions.Item>
                    )}
                    {company.phone && (
                        <Descriptions.Item label="Phone">{company.phone}</Descriptions.Item>
                    )}
                    {company.address && (
                        <Descriptions.Item label="Address" span={2}>{company.address}</Descriptions.Item>
                    )}
                </Descriptions>
            </div>

            <div style={styles.section}>
                <h3 style={styles.sectionTitle}>Projects</h3>
                <Table
                    columns={projectColumns}
                    dataSource={projects}
                    variant="bordered"
                    rowKey="id"
                    onRow={onRowClick}
                    pagination={projects.length > 10 ? { pageSize: 10 } : false}
                />
            </div>
        </div>
    );
};

export default ClientCompanyDetail;
